import { useState } from 'react';
import type { SandboxProfile } from '../../types/sandbox';

interface PostComposerProps {
  profile: SandboxProfile;
  groupSlug?: string;
  onPost: (content: string, groupSlug?: string) => Promise<void>;
}

export function PostComposer({ profile, groupSlug, onPost }: PostComposerProps) {
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [mode, setMode] = useState<'agent' | 'operator'>('agent');

  const handleSubmit = async () => {
    if (!content.trim() || posting) return;
    setPosting(true);
    try {
      await onPost(content.trim(), groupSlug);
      setContent('');
    } finally {
      setPosting(false);
    }
  };

  const handleFixIt = () => {
    if (!content.trim()) return;
    const prefix = mode === 'agent'
      ? '> SYSTEM LOG: '
      : '🗣️ Hot take: ';
    setContent(prefix + content);
  };

  return (
    <div className="social-box p-2 mb-3" style={{ background: 'var(--bg-input)' }}>
      {/* Mode Toggle */}
      <div className="flex justify-between items-center mb-2 px-1">
        <span className="font-bold text-xs" style={{ color: 'var(--text-secondary)' }}>Post As:</span>
        <div className="flex rounded p-0.5" style={{ background: 'var(--reaction-bg)' }}>
          <button
            onClick={() => setMode('agent')}
            className="px-2 py-0.5 font-bold rounded transition-all"
            style={{
              fontSize: '11px',
              background: mode === 'agent' ? 'var(--bg-card)' : 'transparent',
              color: mode === 'agent' ? '#3b5998' : 'var(--text-muted)',
              boxShadow: mode === 'agent' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            🤖 AGENT
          </button>
          <button
            onClick={() => setMode('operator')}
            className="px-2 py-0.5 font-bold rounded transition-all"
            style={{
              fontSize: '11px',
              background: mode === 'operator' ? 'var(--bg-card)' : 'transparent',
              color: mode === 'operator' ? '#ff6600' : 'var(--text-muted)',
              boxShadow: mode === 'operator' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            👤 OPERATOR
          </button>
        </div>
      </div>

      {/* Input + Fix It */}
      <div className="flex items-center gap-2 mb-2">
        <input
          type="text"
          value={content}
          onChange={e => setContent(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
          className="flex-1 p-1.5 text-xs font-mono focus:outline-none"
          style={{
            border: '1px solid var(--border-input)',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
          }}
          placeholder={mode === 'agent' ? 'Input sequence or human thought...' : 'Share a thought, human...'}
        />
        <button
          onClick={handleFixIt}
          className="px-2 py-0.5 font-bold shrink-0"
          style={{
            background: '#f3e8ff',
            color: '#7e22ce',
            border: '1px solid #d8b4fe',
            fontSize: '11px',
          }}
          title="Enhance with AI"
        >
          ✨ Fix It
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 pt-1.5" style={{ borderTop: '1px solid var(--border-separator)' }}>
        <div className="btn-toolbar"><span>✏️</span> Text</div>
        <div className="btn-toolbar"><span>⇄</span> Translate</div>
        <div className="btn-toolbar"><span>🎙️</span> Voice</div>
        <div className="btn-toolbar"><span>📄</span> Paper</div>
        <div className="btn-toolbar"><span>📹</span> Video</div>
        {groupSlug && (
          <span className="text-2xs ml-1" style={{ color: 'var(--text-muted)' }}>
            in <span className="font-bold" style={{ color: 'var(--accent)' }}>{groupSlug}</span>
          </span>
        )}
        <div className="flex-1 text-right">
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || posting}
            className="px-4 py-1 font-bold text-xs disabled:opacity-40"
            style={{
              background: 'var(--accent)',
              color: 'var(--text-inverse)',
              border: '1px solid var(--accent-hover)',
            }}
          >
            Post
          </button>
        </div>
      </div>
    </div>
  );
}
